import React, { useEffect, useState } from 'react';
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Textarea,
  Select,
} from '@chakra-ui/react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import toast from 'react-hot-toast';
import { getUserData } from 'utils';
import { createAPIEndPoint } from 'config/api/api';
import { useNavigate } from 'react-router-dom';
import { logoutUser } from 'utils';
import Loader from 'loaders/Loader';

const AIAgentModal = ({ isOpen, onClose, type = 'add', assistantId = null, fetchAgain }) => {
  const navigate = useNavigate()
  const userData = getUserData();
  const clinic_id = userData?.clinic_id ?? 1;
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingAssistant, setFetchingAssitant] = useState(false);

  const [initialData, setInitialData] = useState(null);


  const fetchAssistant = async () => {
    try {
      setFetchingAssitant(true)
      const response = await createAPIEndPoint(`assistants/`).fetchById(assistantId);
      setInitialData(response.data);
    } catch (err) {
      toast.error('Error fetching assistant data');
    } finally {
      setFetchingAssitant(false)
    }
  };

  useEffect(() => {
    if (assistantId && type === 'edit') {
      fetchAssistant();
    }
  }, [assistantId, isOpen]);

  const fetchClinics = async () => {
    try {
      const response = await createAPIEndPoint('clinic').fetchAll(); // Still fetching assistants
      setClinics(response.data.clinics || []);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Error fetching clinics');
      if (err?.response?.data?.error?.includes('Bearer token')) {
        logoutUser(navigate);
      }
    }
  };

  useEffect(() => {
    fetchClinics();
  }, []);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      clinic_id: initialData?.clinic_id?.toString() || '',
      service_type: initialData?.service_type || '',
      assistant_type: initialData?.type || '',
      name: initialData?.name || '',
      instructions: initialData?.instructions || '',
      model: initialData?.model || 'gpt-4',
      temperature: parseFloat(initialData?.temperature) || 0.5,
    },
    validationSchema: Yup.object({
      clinic_id: Yup.string()
        .required('Please select a clinic.'),
      service_type: Yup.string().required('Please select a service type.'),
      assistant_type: Yup.string().required('Please select a assistant type.'),
      name: Yup.string()
        .required('Assistant name is required.'),
      instructions: Yup.string()
        .required('Please provide assistant instructions.'),
      model: Yup.string()
        .required('Model selection is required.'),
      temperature: Yup.number()
        .transform((value, originalValue) =>
          typeof originalValue === 'string' && originalValue.trim() === ''
            ? undefined
            : value
        )
        .typeError('Temperature must be a valid number.')
        .min(0, 'Temperature must be at least 0.')
        .max(1, 'Temperature must be at most 1.')
        .required('Temperature is required.'),

    }),

    onSubmit: async (values, { resetForm }) => {
      try {
        setLoading(true);
        if (type === 'edit') {
          const selectedClinic = clinics.find(
            (c) => c.id.toString() === values.clinic_id.toString()
          );

          const payload = {
            ...values,
            clinic_id: parseInt(values.clinic_id),
            clinic_name: selectedClinic?.clinic_name || '',
          };

          await createAPIEndPoint(`assistants/`).update(assistantId, payload);
        }
        else {
          const selectedClinic = clinics.find((c) => c.id.toString() === values.clinic_id.toString());
          const payload = {
            ...values,
            clinic_name: selectedClinic?.clinic_name || '',
            clinic_id: selectedClinic?.id,
          };
          await createAPIEndPoint('CreateAssistant').create(payload);
          toast.success('Assistant created');
        }

        resetForm();
        onClose();
        fetchAgain?.();
      } catch (error) {
        console.error(error);
        toast.error(error.response?.data?.error || 'Something went wrong');
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <Modal size="6xl" isOpen={isOpen} onClose={() => {
      onClose();
      formik.resetForm();
      setInitialData([]);
    }}
      isCentered>
      <ModalOverlay />
      <ModalContent borderRadius="16px" maxWidth={{ base: '90%', md: '448px' }} pb="16px">
        {fetchingAssistant && <Loader isAbsolute={true} />}
        <ModalHeader color="brand.500">
          {type === 'edit' ? 'Edit Assistant' : 'Add Assistant'}
        </ModalHeader>
        <ModalCloseButton />
        <form onSubmit={formik.handleSubmit}>
          <ModalBody maxHeight="65vh" overflowY="auto" pb={2}>
            {/* Clinic Dropdown */}
            <FormControl isInvalid={formik.touched.clinic_id && formik.errors.clinic_id} mb={3}>
              <FormLabel>Clinic</FormLabel>
              <Select
                name="clinic_id"
                value={formik.values.clinic_id}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                fontSize="sm"
                fontWeight="500"
                variant="auth"
                placeholder="Select Clinic"
              >
                {clinics.map((c) => (
                  <option key={c.id} value={c.id}>{c.clinic_name}</option>
                ))}
              </Select>
              {formik.touched.clinic_id && formik.errors.clinic_id && (
                <Text color="red.500" fontSize="sm">{formik.errors.clinic_id}</Text>
              )}
            </FormControl>

            <FormControl isInvalid={formik.touched.service_type && formik.errors.service_type} mb={3}>
              <FormLabel>Service Type</FormLabel>
              <Select
                name="service_type"
                value={formik.values.service_type}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                fontSize="sm"
                fontWeight="500"
                variant="auth"
                placeholder="Select service type"
              >
                <option value="openai">Open AI</option>
                <option value="deepgram">Deepgram</option>
              </Select>
              {formik.touched.service_type && formik.errors.service_type && (
                <Text color="red.500" fontSize="sm">{formik.errors.service_type}</Text>
              )}
            </FormControl>

            <FormControl isInvalid={formik.touched.assistant_type && formik.errors.assistant_type} mb={3}>
              <FormLabel>Assistant Type</FormLabel>
              <Select
                name="assistant_type"
                value={formik.values.assistant_type}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                fontSize="sm"
                fontWeight="500"
                variant="auth"
                placeholder="Select type"
              >
                <option value="email">Email</option>
                <option value="chat">Chat</option>
                <option value="sms">SMS</option>
                <option value="text">Text</option>
              </Select>
              {formik.touched.assistant_type && formik.errors.assistant_type && (
                <Text color="red.500" fontSize="sm">{formik.errors.assistant_type}</Text>
              )}
            </FormControl>


            {['name', 'instructions', 'model', 'temperature'].map((field, idx) => {
              const isInvalid = formik.touched[field] && formik.errors[field];
              const commonProps = {
                name: field,
                fontSize: "sm",
                fontWeight: "500",
                value: formik.values[field],
                onBlur: formik.handleBlur,
              };

              return (
                <FormControl key={idx} isInvalid={isInvalid} mb={3}>
                  <FormLabel textTransform="capitalize">{field}</FormLabel>

                  {field === 'instructions' ? (
                    <Textarea
                      {...commonProps}
                      placeholder="Enter instructions"
                      resize="vertical"
                      minH="120px"
                      onChange={formik.handleChange}
                    />
                  ) : field === 'temperature' ? (
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      max="1"
                      placeholder="Enter temperature"
                      variant="auth"
                      {...commonProps}
                      onChange={(e) => {
                        const value = e.target.value === '' ? '' : parseFloat(e.target.value);
                        formik.setFieldValue(field, value);
                      }}
                    />
                  ) : (
                    <Input
                      placeholder={`Enter ${field}`}
                      variant="auth"
                      {...commonProps}
                      onChange={formik.handleChange}
                    />
                  )}

                  {isInvalid && (
                    <Text color="red.500" fontSize="sm">
                      {formik.errors[field]}
                    </Text>
                  )}
                </FormControl>
              );
            })}


            {/* Hidden clinic_id input */}
            {/* <Input type="hidden" name="clinic_id" value={formik.values.clinic_id} /> */}


          </ModalBody>
          <ModalFooter pt={2}>
            <Button
              isLoading={loading}
              type="submit"
              colorScheme="brand"
              w="100%"
              mt={4}
            >
              {type === 'edit' ? 'Update Assistant' : 'Add Assistant'}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default AIAgentModal;
